import { AlertTriangle, UserRoundCog, CalendarDays, ShieldAlert } from "lucide-react";

// The four operational views. Each is a shortcut that sets one existing
// filter dimension to a specific value, never a parallel "view" concept or a
// new status: "En retard" is statusFilter==="en_retard", "Mes tâches" is
// responsableFilter==="moi" (harmless for a non-scope_all user, whose
// GET /tasks already returns only their tasks), "Cette semaine"/"Critiques"
// are dueDateFilter/priorityFilter values. Toggling a tile off resets only
// that dimension to "all", so the views combine with the rest of the bar.
export function OperationalViewsRow({
  counts, responsableFilter, setResponsableFilter, dueDateFilter, setDueDateFilter,
  statusFilter, setStatusFilter, priorityFilter, setPriorityFilter,
}) {
  const tiles = [
    {
      id: "mine", label: "Mes tâches", value: counts.mine, icon: UserRoundCog, color: "text-slate-600",
      active: responsableFilter === "moi",
      onClick: () => setResponsableFilter(responsableFilter === "moi" ? "all" : "moi"),
    },
    {
      id: "week", label: "Cette semaine", value: counts.thisWeek, icon: CalendarDays, color: "text-blue-600",
      active: dueDateFilter === "this_week",
      onClick: () => setDueDateFilter(dueDateFilter === "this_week" ? "all" : "this_week"),
    },
    {
      id: "en_retard", label: "En retard", value: counts.enRetard, icon: AlertTriangle, color: "text-red-600",
      active: statusFilter === "en_retard",
      onClick: () => setStatusFilter(statusFilter === "en_retard" ? "all" : "en_retard"),
    },
    {
      id: "critical", label: "Critiques", value: counts.critical, icon: ShieldAlert, color: "text-red-600",
      active: priorityFilter === "critical",
      onClick: () => setPriorityFilter(priorityFilter === "critical" ? "all" : "critical"),
    },
  ];
  // Hidden below md: every dimension it shortcuts stays reachable on mobile
  // through the "Filtres" popover and the status tabs ("Mes tâches" ->
  // Responsable=Moi, "Cette semaine" -> Échéance, "En retard" -> the tabs,
  // "Critiques" -> Priorité).
  return (
    <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-2">
      {tiles.map((tile) => (
        <button
          key={tile.id}
          type="button"
          onClick={tile.onClick}
          data-testid={`view-${tile.id}`}
          className={`flex items-center gap-2 text-left px-3 py-2 rounded-lg border transition ${tile.active ? "border-jamora bg-jamora/5" : "border-slate-200 bg-white hover:border-slate-300"}`}
        >
          <tile.icon size={14} className={`${tile.color} shrink-0`} />
          <span className="text-sm font-display font-bold text-slate-900">{tile.value}</span>
          <span className="text-xs text-slate-500 truncate">{tile.label}</span>
        </button>
      ))}
    </div>
  );
}

// Compact 3-option segmented control, deliberately not a second <Tabs>
// (already used just below for Statut) so "view" and "statut" stay visually
// and semantically distinct. Tasks.jsx owns `view` and reflects it in ?view=.
export function ViewToggle({ view, onChange }) {
  const options = [{ id: "list", label: "Liste" }, { id: "timeline", label: "Timeline" }, { id: "kanban", label: "Kanban" }];
  return (
    <div
      className="flex md:inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-0.5 w-full md:w-auto"
      role="tablist" aria-label="Vue des tâches"
    >
      {options.map((opt) => (
        <button
          key={opt.id} type="button" role="tab" aria-selected={view === opt.id}
          onClick={() => onChange(opt.id)} data-testid={`view-toggle-${opt.id}`}
          className={`flex-1 md:flex-none px-3 py-1.5 text-sm font-medium rounded-md transition ${
            view === opt.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
